// FORGED Ed Systems — DEPLOY004d — v0.4.4 — ForgotPasswordPage.tsx
// Last touched: 2026-05-20
//
// /forgot-password — teacher enters their email, Supabase sends them a
// password-recovery email with a link to /reset-password. Sender configured
// in Supabase dashboard under Authentication → Email Templates.

import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "./supabaseClient";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);

    // redirectTo points back to our reset-password page. Supabase appends a
    // recovery token in the URL hash; ResetPasswordPage detects it.
    const redirectTo = window.location.origin + "/reset-password";
    const res = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    setSubmitting(false);

    if (res.error) {
      setErrorMsg(res.error.message);
      return;
    }
    setSent(true);
  }

  if (sent) {
    return (
      <main className="container">
        <header className="brand-header">
          <h1>Check your email</h1>
          <p className="tagline">
            We've sent a password reset link to <code>{email}</code>.
          </p>
        </header>

        <section className="hero">
          <div className="info-card">
            <p style={{ marginTop: 0 }}>
              Click the link in the email to set a new password. The link
              expires in 1 hour.
            </p>
            <p>
              Didn't get it? Check your spam folder, or try again with the
              correct email address. If your email isn't registered with
              forgedsight, no message is sent (that's intentional — we don't
              confirm or deny account existence).
            </p>
          </div>

          <div className="cta-row" style={{ marginTop: "1.5rem" }}>
            <Link to="/sign-in" className="cta secondary">
              ← Back to sign in
            </Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="container">
      <header className="brand-header">
        <h1>Reset your password</h1>
        <p className="tagline">
          Enter your email and we'll send you a link to set a new password.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="form-stack" style={{ maxWidth: "440px" }}>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
          />
        </label>

        {errorMsg && <div className="error-banner">{errorMsg}</div>}

        <button type="submit" className="cta primary" disabled={submitting}>
          {submitting ? "Sending…" : "Send reset link"}
        </button>

        <p style={{ color: "var(--color-text-muted)" }}>
          Remembered it? <Link to="/sign-in">Sign in instead</Link>
        </p>
      </form>
    </main>
  );
}
